import { readFileSync } from "node:fs";
import { readdir, readFile, rename, rm } from "node:fs/promises";
import { ensureSecureDir, writeSecureFile } from "./secure-file.js";
import { join } from "node:path";


const SYNC_LOCK_STALE_MS = 5 * 60 * 1000; // 5 minutes

function syncLockPath(dataDir: string, channelId: string): string {
  const safe = channelId.replace(/[^a-zA-Z0-9_-]/g, "_");
  return join(dataDir, `mls-a2a-sync-${safe}.lock`);
}

/**
 * Acquire a channel-level A2A sync lock. Returns true if acquired,
 * false if another instance holds the lock. Uses atomic exclusive-create
 * (wx flag) to prevent TOCTOU races.
 *
 * Stale locks (>5 min old) are automatically cleaned up. This handles
 * gateway crashes between lock acquisition and Welcome processing.
 * After a crash, the first sync attempt on restart may encounter a stale
 * lock — it will be deleted and the lock re-acquired on the same call.
 */
export async function acquireA2ASyncLock(
  dataDir: string,
  channelId: string,
): Promise<boolean> {
  await ensureSecureDir(dataDir);
  const lockFile = syncLockPath(dataDir, channelId);
  const content = JSON.stringify({ pid: process.pid, timestamp: Date.now() });
  try {
    await writeSecureFile(lockFile, content, "wx");
    return true;
  } catch (err: any) {
    if (err.code !== "EEXIST") throw err;
    // Lock exists — check staleness
    try {
      const raw = await readFile(lockFile, "utf-8");
      const parsed = JSON.parse(raw);
      if (typeof parsed.timestamp === "number" && Date.now() - parsed.timestamp > SYNC_LOCK_STALE_MS) {
        // Stale lock — clean up and retry once
        await rm(lockFile).catch(() => {});
        try {
          await writeSecureFile(lockFile, content, "wx");
          return true;
        } catch {
          return false; // Another instance beat us to it
        }
      }
    } catch {
      // Can't read lock file — treat as held
    }
    return false;
  }
}

/**
 * Release the channel-level A2A sync lock. Idempotent — no error if
 * the lock file does not exist.
 */
export async function releaseA2ASyncLock(
  dataDir: string,
  channelId: string,
): Promise<void> {
  try {
    await rm(syncLockPath(dataDir, channelId));
  } catch { /* ignore ENOENT */ }
}

function mlsFileName(groupId: string): string {
  // Sanitize groupId for filesystem safety
  const safe = groupId.replace(/[^a-zA-Z0-9_-]/g, "_");
  return `mls-${safe}.json`;
}

const pendingKpPath = (dataDir: string, groupId: string) =>
  join(dataDir, `mls-kp-pending-${groupId.replace(/[^a-zA-Z0-9_-]/g, "_")}.json`);

/**
 * Check if a pending Welcome KP bundle already exists for a group.
 */
export async function hasPendingWelcome(
  dataDir: string,
  groupId: string,
): Promise<boolean> {
  try {
    await readFile(pendingKpPath(dataDir, groupId));
    return true;
  } catch {
    return false;
  }
}

/**
 * Save a pending KeyPackage bundle to disk.
 * The KP bundle has complex typed fields — we serialize the private key material
 * (raw Uint8Arrays) to base64, and the public package as pre-serialized bytes.
 * `serializePublicFn` should be MLSGroupManager.serializeKeyPackage.
 */
export async function savePendingKpBundle(
  dataDir: string,
  groupId: string,
  kp: any,
  serializePublicFn: (pub: any) => Uint8Array,
): Promise<void> {
  await ensureSecureDir(dataDir);
  const serialized = {
    publicPackageBytes: Buffer.from(serializePublicFn(kp.publicPackage)).toString("base64"),
    privatePackage: {
      initPrivateKey: Buffer.from(kp.privatePackage.initPrivateKey).toString("base64"),
      hpkePrivateKey: Buffer.from(kp.privatePackage.hpkePrivateKey).toString("base64"),
      signaturePrivateKey: Buffer.from(kp.privatePackage.signaturePrivateKey).toString("base64"),
    },
  };
  await writeSecureFile(pendingKpPath(dataDir, groupId), JSON.stringify(serialized));
}

/**
 * Load a pending KeyPackage bundle from disk.
 * Returns the raw private key material as Uint8Arrays.
 * The public package is returned as raw serialized bytes — the caller
 * must deserialize it if needed, or the Welcome handler can use just the private keys.
 */
export async function loadPendingKpBundle(
  dataDir: string,
  groupId: string,
): Promise<any | null> {
  try {
    const raw = await readFile(pendingKpPath(dataDir, groupId), "utf-8");
    const parsed = JSON.parse(raw);
    return {
      publicPackageBytes: new Uint8Array(Buffer.from(parsed.publicPackageBytes, "base64")),
      privatePackage: {
        initPrivateKey: new Uint8Array(Buffer.from(parsed.privatePackage.initPrivateKey, "base64")),
        hpkePrivateKey: new Uint8Array(Buffer.from(parsed.privatePackage.hpkePrivateKey, "base64")),
        signaturePrivateKey: new Uint8Array(Buffer.from(parsed.privatePackage.signaturePrivateKey, "base64")),
      },
    };
  } catch {
    return null;
  }
}

/**
 * Clear the pending KP bundle (after successful Welcome join).
 */
export async function clearPendingWelcome(
  dataDir: string,
  groupId: string,
): Promise<void> {
  try {
    await rm(pendingKpPath(dataDir, groupId));
  } catch { /* ignore */ }
}

/**
 * Save MLS group state to a per-group file.
 * State is an opaque serialized string from MLSGroupManager.exportState().
 */
export async function saveMlsState(
  dataDir: string,
  groupId: string,
  state: string,
): Promise<void> {
  await ensureSecureDir(dataDir);
  const filePath = join(dataDir, mlsFileName(groupId));
  await writeSecureFile(filePath, state);
}

/**
 * Load MLS group state from a per-group file.
 * Returns null if the file doesn't exist (first join or after re-join cleanup).
 */
export async function loadMlsState(
  dataDir: string,
  groupId: string,
): Promise<string | null> {
  try {
    const filePath = join(dataDir, mlsFileName(groupId));
    return await readFile(filePath, "utf-8");
  } catch {
    return null;
  }
}

/**
 * Delete MLS group state file.
 * Used when re-joining a group (old state is invalid after re-join).
 */
export async function deleteMlsState(
  dataDir: string,
  groupId: string,
): Promise<void> {
  try {
    const filePath = join(dataDir, mlsFileName(groupId));
    await rm(filePath);
  } catch {
    // Already removed or never existed
  }
}

/**
 * #629 — quarantine local MLS state for groups this device is no longer in.
 *
 * Investigated 2026-08-04: loopita's bridge held 1,383 MLS errors
 * (`Desired gen in the past`, `invalid ghash tag`) across 13 rooms. Every one of
 * those rooms was DISBANDED with zero active members, and the device's own
 * membership was `left`. The crypto was behaving correctly — it was being handed
 * commits for state it had legitimately moved past. The generator is local state
 * outliving the membership that justified it.
 *
 * Still live at the time of writing: of three local group-state files, one
 * belonged to a group the device is not in the roster of, and one to a group the
 * server has never heard of.
 *
 * QUARANTINE, NOT DELETE. Removing MLS state is irreversible — it destroys the
 * ability to decrypt that group's history. #606 phase 2 set the precedent for
 * cleanup in this codebase (default-OFF, never empties a roster, capped), and
 * this follows it: the file is RENAMED aside, so a wrong call is recoverable
 * with `mv`. Deleting would make a false positive permanent.
 *
 * Returns the groupIds quarantined, for the caller to log.
 */
export async function quarantineOrphanedMlsState(
  dataDir: string,
  /**
   * Returns true if this device should still hold state for `groupId`.
   * Injected rather than fetched here so the decision — and its failure
   * modes — stay testable without a network.
   */
  isStillMember: (groupId: string) => Promise<boolean | null>,
  opts: { max?: number } = {},
): Promise<string[]> {
  const max = opts.max ?? 20; // bounded, like #606 phase 2's prune
  const quarantined: string[] = [];

  let names: string[];
  try {
    names = await readdir(dataDir);
  } catch {
    return quarantined; // no data dir yet — nothing to reconcile
  }

  for (const name of names) {
    if (quarantined.length >= max) break;
    // Only real group state. `mls-kp-pending-*` are unconsumed KeyPackages,
    // NOT group state: quarantining one would strand the Welcome it is waiting
    // for, which is the opposite of the fix.
    const m = /^mls-(?!kp-pending-)([A-Za-z0-9_-]+)\.json$/.exec(name);
    if (!m) continue;
    const groupId = m[1];

    let member: boolean | null;
    try {
      member = await isStillMember(groupId);
    } catch {
      continue; // unknown — never quarantine on an error
    }
    // null = could not determine. Leave it. A transient API failure must not
    // look like "you were removed"; that is how a working agent loses history.
    if (member === null || member === true) continue;

    try {
      await rename(join(dataDir, name), join(dataDir, `${name}.orphaned`));
      quarantined.push(groupId);
    } catch {
      // Could not move it — leave it in place rather than half-acting.
    }
  }

  return quarantined;
}


/**
 * #1518 — ids of MLS application frames this agent has decrypted, kept across
 * restarts so a post-restart replay is recognised. Read synchronously once;
 * a missing or unreadable file is an empty list.
 */
const DECRYPTED_IDS_FILE = "decrypted-message-ids.json";

export function loadDecryptedMessageIdsSync(dataDir: string): string[] {
  try {
    const raw = JSON.parse(readFileSync(join(dataDir, DECRYPTED_IDS_FILE), "utf8"));
    return Array.isArray(raw) ? raw.filter((id): id is string => typeof id === "string" && id !== "") : [];
  } catch {
    return [];
  }
}

export async function saveDecryptedMessageIds(dataDir: string, ids: string[]): Promise<void> {
  await ensureSecureDir(dataDir);
  await writeSecureFile(join(dataDir, DECRYPTED_IDS_FILE), JSON.stringify(ids));
}
